import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "@/lib/api";
import { toast } from "sonner";

/**
 * Frontend-hosted Microsoft OAuth callback (`/auth/microsoft/callback`).
 *
 * Our platform routes everything without `/api/` to the frontend, so the
 * redirect URI we registered in Azure lands here. We extract `code` +
 * `state` from the query string, POST them to the backend at
 * `/api/outlook/auth/complete`, and bounce the user to
 * `/settings/integrations` with a status flag so the page can show the
 * right toast.
 */
export default function MicrosoftAuthCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [message, setMessage] = useState("Finishing Outlook connection…");

  useEffect(() => {
    const errorParam = params.get("error");
    if (errorParam) {
      const desc = params.get("error_description") || errorParam;
      toast.error(`Outlook connect failed: ${desc}`);
      navigate(`/settings/integrations?outlook=${encodeURIComponent(errorParam)}`, { replace: true });
      return;
    }
    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state) {
      toast.error("Outlook connect failed: missing code or state");
      navigate("/settings/integrations?outlook=missing_params", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await API.post("/outlook/auth/complete", { code, state });
        if (data.ok) {
          navigate(`/settings/integrations?outlook=connected${data.email ? `&as=${encodeURIComponent(data.email)}` : ""}`, { replace: true });
        } else {
          const err = data.error || "unknown";
          setMessage(`Connection failed: ${err}`);
          navigate(`/settings/integrations?outlook=${encodeURIComponent(err)}&detail=${encodeURIComponent(data.detail || "")}`, { replace: true });
        }
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || "unknown";
        setMessage(`Connection failed: ${msg}`);
        navigate(`/settings/integrations?outlook=error&detail=${encodeURIComponent(msg)}`, { replace: true });
      }
    })();
  }, [params, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center max-w-sm w-full">
        <div className="w-12 h-12 mx-auto rounded-full bg-blue-50 flex items-center justify-center mb-4">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
        <p className="text-sm text-slate-700" data-testid="microsoft-callback-status">{message}</p>
      </div>
    </div>
  );
}
